<template>
  <div class="cardGraphicComponent" ref="chart" />
</template>

<script>
import * as echarts from "echarts";
import api from "../../services/axios";
import moment from "moment";
import "moment/locale/pt";

export default {
  props: {
    selectedDate: {
      type: String,
      default: () => moment().format("YYYY-MM-DD"),
    },
    hideValues: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chart: null,
      resizeHandler: null,
      option: {
        series: [{ data: [] }, { data: [] }],
      },
    };
  },
  methods: {
    toNumber(value) {
      if (typeof value === "number") return value;
      if (value === null || value === undefined || value === "") return 0;

      const normalizedValue = String(value)
        .replace(/[^\d,.-]/g, "")
        .replace(/\.(?=\d{3}(,|$))/g, "")
        .replace(",", ".");

      const numberValue = Number(normalizedValue);
      return Number.isFinite(numberValue) ? numberValue : 0;
    },
    async readBillsByType(typeBill, startDate, endDate) {
      let page = 1;
      let hasNextPage = true;
      const totalsByDay = {};
      const seenBills = new Set();

      while (hasNextPage && page <= 100) {
        const qs = [
          `startDate=${startDate}`,
          `endDate=${endDate}`,
          `typeBill=${typeBill}`,
          "q=",
          "collumnSearch=",
          "idCategoryBill=",
          "status=",
          `page=${page}`,
        ].join("&");
        const { data } = await api.get(`/bills/list/bills/?${qs}`);
        const bills = Array.isArray(data?.bills) ? data.bills : [];
        let newBillsCount = 0;

        bills.forEach((bill) => {
          const billKey = bill?.id || bill?.uuid || JSON.stringify(bill);
          if (seenBills.has(billKey)) return;
          seenBills.add(billKey);
          newBillsCount += 1;

          const day = moment(bill.data).format("YYYY-MM-DD");
          totalsByDay[day] = (totalsByDay[day] || 0) + this.toNumber(bill.valorTotal);
        });

        hasNextPage = bills.length > 0 && newBillsCount > 0;
        page += 1;
      }

      return Object.entries(totalsByDay);
    },
    async readBills(startDate, endDate) {
      try {
        const [receivedData, payableData] = await Promise.all([
          this.readBillsByType("entrada", startDate, endDate),
          this.readBillsByType("saida", startDate, endDate),
        ]);

        this.option.series[0].data = receivedData;
        this.option.series[1].data = payableData;
      } catch (error) {
        console.error(error);
        this.option.series[0].data = [];
        this.option.series[1].data = [];
      }
    },
    getMonthDays() {
      const selectedMonth = moment(this.selectedDate);
      const currentDate = selectedMonth.clone().startOf("month");
      const lastDay = selectedMonth.clone().endOf("month");
      const days = [];

      while (currentDate.isSameOrBefore(lastDay, "day")) {
        days.push(currentDate.format("YYYY-MM-DD"));
        currentDate.add(1, "day");
      }

      return days;
    },
    convertData(data, days) {
      const valuesByDay = data.reduce((acc, item) => {
        acc[moment(item[0]).format("YYYY-MM-DD")] = item[1] || 0;
        return acc;
      }, {});

      return days.map((day) => valuesByDay[day] || 0);
    },
    applyGraphOptions(days) {
      if (!this.chart) return;

      this.chart.setOption({
        legend: {
          padding: 10,
          top: "bottom",
          data: ["A receber", "A pagar"],
        },
        tooltip: this.getTooltipOptions(),
        xAxis: {
          type: "category",
          data: days,
          axisPointer: {
            value: this.selectedDate,
            snap: true,
            lineStyle: {
              color: "#7581BD",
              width: 1,
            },
            label: {
              show: true,
              fontSize: 12,
              formatter: function (params) {
                return moment(params.value).format("DD/MM/YYYY");
              },
              backgroundColor: "#7581BD",
            },
            handle: {
              show: true,
              color: "#7581BD",
              size: 25,
              margin: 40,
            },
          },
          splitLine: {
            show: false,
          },
          axisLabel: {
            formatter: function (value) {
              return moment(value).format("DD");
            },
          },
        },
        yAxis: {
          type: "value",
          axisTick: {
            inside: true,
          },
          splitLine: {
            show: false,
          },
          axisLabel: {
            inside: true,
            formatter: this.hideValues ? "••••\n" : "{value}\n",
          },
          z: 10,
        },
        grid: {
          top: 18,
          left: 15,
          right: 15,
          bottom: 56,
          containLabel: true,
        },
        series: [
          {
            name: "A receber",
            type: "line",
            smooth: false,
            symbol: "circle",
            symbolSize: 7,
            itemStyle: {
              color: "#0770FF",
            },
            lineStyle: {
              width: 3,
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: "rgba(58,77,233,0.4)",
                },
                {
                  offset: 1,
                  color: "rgba(58,77,233,0.1)",
                },
              ]),
            },
            data: this.option.series[0].data,
          },
          {
            name: "A pagar",
            type: "line",
            smooth: false,
            symbol: "circle",
            symbolSize: 7,
            itemStyle: {
              color: "#F2597F",
            },
            lineStyle: {
              width: 3,
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: "rgba(213,72,120,0.4)",
                },
                {
                  offset: 1,
                  color: "rgba(213,72,120,0.1)",
                },
              ]),
            },
            data: this.option.series[1].data,
          },
        ],
      }, true);
      this.chart.resize();
    },
    getTooltipOptions() {
      return {
        trigger: "axis",
        formatter: (params) => {
          const title = moment(params[0].axisValue).format("DD/MM/YYYY");
          const rows = params
            .map((item) => {
              if (this.hideValues) {
                return `${item.marker}${item.seriesName}: <strong>R$ •••••</strong>`;
              }

              const value = Number(item.value || 0).toLocaleString("pt-br", {
                style: "currency",
                currency: "BRL",
              });
              return `${item.marker}${item.seriesName}: <strong>${value}</strong>`;
            })
            .join("<br/>");

          return `${title}<br/>${rows}`;
        },
      };
    },
    async refreshGraph() {
      const days = this.getMonthDays();
      await this.readBills(days[0], days[days.length - 1]);

      const sortedReceivedData = this.convertData(this.option.series[0].data, days);
      const sortedPayableData = this.convertData(this.option.series[1].data, days);

      this.option.series[0].data = sortedReceivedData;
      this.option.series[1].data = sortedPayableData;
      this.applyGraphOptions(days);
    },
  },
  async mounted() {
    await this.$nextTick();
    this.chart = echarts.init(this.$refs.chart);
    await this.refreshGraph();
    this.resizeHandler = () => this.chart && this.chart.resize();
    window.addEventListener("resize", this.resizeHandler);
  },
  watch: {
    selectedDate() {
      this.refreshGraph();
    },
    hideValues() {
      this.applyGraphOptions(this.getMonthDays());
    },
  },
  beforeDestroy() {
    if (this.chart) {
      window.removeEventListener("resize", this.resizeHandler);
      this.chart.dispose();
    }
  },
};
</script>
<style scoped>
.cardGraphicComponent {
  width: 100%;
  min-width: 0;
  height: 100%;
  min-height: 245px;
}
</style>
